import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, LayoutList, LayoutDashboard, Paperclip, Users } from 'lucide-react'
import { projectsApi } from '../api/projects'
import { issuesApi } from '../api/issues'
import { useAuthStore } from '../store/authStore'
import { canCreateMainIssue, canDeleteIssue, canMoveIssue, hasFullAccess } from '../lib/roles'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { Avatar } from '../components/common/Avatar'
import { Button } from '../components/common/Button'
import IssueForm, { type CreateIssuePayload } from '../components/issues/IssueForm'
import IssueFiltersBar from '../components/issues/IssueFiltersBar'
import ReadyForNextStageModal from '../components/issues/ReadyForNextStageModal'
import IssueSheetActions from '../components/issues/IssueSheetActions'
import { insertIssueIntoProjectCaches, syncIssueAcrossProjectCaches } from '../lib/issueCache'
import { stripPendingInlineMedia } from '../lib/issueDescription'
import { finalizeIssueAttachmentsAndInlineMedia } from '../lib/issueSubmission'
import { invalidateIssueViews } from '../lib/queryInvalidation'
import { requiresStatusDetailsPopup } from '../lib/readyForNextStage'
import { submitStatusTransitionWithInternalNote } from '../lib/statusTransitionSubmission'
import type { IssueFilters } from '../api/issues'
import type { Issue, IssueStatus } from '../types'

function KanbanCard({
  issue,
  isDragging = false,
  canDrag = true,
}: {
  issue: Issue
  isDragging?: boolean
  canDrag?: boolean
}) {
  const navigate = useNavigate()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: issue.id, disabled: !canDrag })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
  }

  const latestLabel = issue.labels.length > 0 ? issue.labels[issue.labels.length - 1]?.label : null
  const otherLabels = issue.labels.slice(0, -1).map(({ label }) => label)
  const creatorName = issue.createdBy?.fullName || 'Unknown'
  const creatorShortName = creatorName.split(/\s+/).filter(Boolean).slice(0, 2).join(' ')
  const visibleAssignees = issue.assignees.slice(0, 3)
  const attachmentCount = issue._count?.attachments || 0
  const createdDate = new Date(issue.createdAt)
  const createdLabel = Number.isNaN(createdDate.getTime())
    ? ''
    : createdDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })

  return (
    <div
      ref={setNodeRef}
      onClick={() => navigate(`/issues/${issue.id}`)}
      style={{
        background: isDragging ? '#f0f4ff' : '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '12px',
        cursor: canDrag ? 'grab' : 'pointer',
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
        opacity: isDragging ? 0.5 : 1,
        ...style,
      }}
      {...listeners}
      {...attributes}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: '8px' }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8' }}>
          {issue.identifier}
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            minWidth: 0,
            maxWidth: 190,
          }}
        >
          <span
            title={`Created by ${creatorName}${createdLabel ? ` on ${createdLabel}` : ''}`}
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#64748b',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {creatorShortName}{createdLabel ? ` · ${createdLabel}` : ''}
          </span>
          <PriorityBadge priority={issue.priority} showLabel={false} size="sm" />
        </div>
      </div>
      <p
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#1e293b',
          margin: '0 0 10px',
          lineHeight: '1.4',
          minHeight: '54px',
          maxHeight: '54px',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {issue.title}
      </p>
      {otherLabels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '-3px 0 9px' }}>
          {otherLabels.map((label) => (
            <span key={label.id} title={label.name} style={{ maxWidth: 112, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '1px 5px', borderRadius: 4, border: '1px solid ' + label.color + '44', background: label.color + '18', color: label.color, fontSize: 9, fontWeight: 700, lineHeight: '15px' }}>
              {label.name}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, minHeight: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
          <div
            title={
              issue.assignees.length > 0
                ? `Assignees: ${issue.assignees.map((a) => a.user.fullName).join(', ')}`
                : 'Assignees: Unassigned'
            }
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#94a3b8' }}>
              <Users size={11} />
            </span>
            {visibleAssignees.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                {visibleAssignees.map((a) => (
                  <Avatar key={a.user.id} user={a.user} size="sm" />
                ))}
                {issue.assignees.length > visibleAssignees.length && (
                  <span style={{ fontSize: 10, color: '#64748b', marginLeft: 2, flexShrink: 0 }}>
                    +{issue.assignees.length - visibleAssignees.length}
                  </span>
                )}
              </div>
            ) : (
              <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>Unassigned</span>
            )}
          </div>

          {attachmentCount > 0 && (
            <span
              title={`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: '#64748b',
                fontSize: 10,
                flexShrink: 0,
              }}
            >
              <Paperclip size={11} />
              {attachmentCount}
            </span>
          )}
        </div>
        {latestLabel && (
          <span
            style={{
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '999px',
              background: latestLabel.color + '22',
              color: latestLabel.color,
              border: `1px solid ${latestLabel.color}44`,
              maxWidth: 96,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            title={latestLabel.name}
          >
            {latestLabel.name}
          </span>
        )}
      </div>
    </div>
  )
}

function KanbanColumn({
  status,
  issues,
  onAddClick,
  isClient,
  canDrag,
}: {
  status: IssueStatus
  issues: Issue[]
  onAddClick: () => void
  isClient: boolean
  canDrag: (issue: Issue) => boolean
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id })

  return (
    <div
      style={{
        width: '280px',
        minWidth: '280px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {/* Column header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 4px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: status.color,
              display: 'inline-block',
            }}
          />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{status.name}</span>
          <span
            style={{
              fontSize: '11px',
              background: '#f1f5f9',
              color: '#64748b',
              padding: '1px 6px',
              borderRadius: '10px',
            }}
          >
            {issues.length}
          </span>
        </div>
        {!isClient && (
          <button
            onClick={onAddClick}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              padding: '2px',
              borderRadius: '4px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#6366f1')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minHeight: '120px',
          background: isOver ? '#f0f4ff' : '#f8fafc',
          borderRadius: '8px',
          padding: '8px',
          border: isOver ? '2px dashed #6366f1' : '2px dashed transparent',
          transition: 'all 0.15s',
          flex: 1,
        }}
      >
        <SortableContext items={issues.map((issue) => issue.id)} strategy={verticalListSortingStrategy}>
          {issues.map((issue) => (
            <KanbanCard
              key={issue.id}
              issue={issue}
              canDrag={canDrag(issue)}
            />
          ))}
        </SortableContext>
        {issues.length === 0 && !isOver && (
          <div
            style={{
              textAlign: 'center',
              color: '#cbd5e1',
              fontSize: '12px',
              padding: '24px 0',
            }}
          >
            Drop here
          </div>
        )}
      </div>
    </div>
  )
}

export default function BoardPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const [createOpen, setCreateOpen] = useState(false)
  const [defaultStatusId, setDefaultStatusId] = useState<string | undefined>()
  const [filters, setFilters] = useState<IssueFilters>({})
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null)
  const [readyTransition, setReadyTransition] = useState<{
    issue: Issue
    targetStatusId: string
    targetIndex: number
  } | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getProject(projectId!),
    enabled: !!projectId,
  })

  const { data: statusesData } = useQuery({
    queryKey: ['statuses', projectId],
    queryFn: () => projectsApi.listStatuses(projectId!),
    enabled: !!projectId,
  })

  const { data: membersData } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => projectsApi.listMembers(projectId!),
    enabled: !!projectId,
  })

  const { data: issuesData, isLoading } = useQuery({
    queryKey: ['issues', projectId, filters],
    queryFn: () => issuesApi.listIssues(projectId!, filters),
    enabled: !!projectId,
  })

  const { data: labelsData } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => projectsApi.listLabels(projectId!),
    enabled: !!projectId,
  })

  const updateMutation = useMutation({
    mutationFn: ({ issueId, targetStatusId, targetIndex }: { issueId: string; targetStatusId: string; targetIndex: number }) =>
      issuesApi.reorderIssue(projectId!, { issueId, targetStatusId, targetIndex }),
    onSuccess: async () => {
      await invalidateIssueViews(qc, projectId!)
    },
  })

  const readyForNextStageMutation = useMutation({
    mutationFn: ({ issueId, issueTitle, targetStatusName, targetStatusId, targetIndex, labelIds, timeLog, proof }: {
      issueId: string
      issueTitle: string
      targetStatusName: string
      targetStatusId: string
      targetIndex: number
      labelIds?: string[]
      timeLog?: Parameters<typeof issuesApi.readyForNextStage>[1]['timeLog']
      proof?: Parameters<typeof submitStatusTransitionWithInternalNote>[0]['payload']['proof']
    }) => submitStatusTransitionWithInternalNote({
      issueId,
      issueTitle,
      targetStatusName,
      payload: { targetStatusId, labelIds, timeLog, proof },
    }),
    onSuccess: async (response) => {
      const updatedIssue = response.data.data
      syncIssueAcrossProjectCaches(qc, updatedIssue.projectId, updatedIssue)
      setReadyTransition(null)
      await invalidateIssueViews(qc, updatedIssue.projectId)
    },
  })

  const createMutation = useMutation({
    mutationFn: async ({ attachments, inlineMedia, ...data }: CreateIssuePayload) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const created = await issuesApi.createIssue(projectId!, {
        ...data,
        description: stripPendingInlineMedia(data.description),
      } as any)
      const issueId = created.data.data.id
      const finalDescription = await finalizeIssueAttachmentsAndInlineMedia({
        issueId,
        description: data.description,
        attachments,
        inlineMedia,
      })
      if (finalDescription !== data.description) {
        return issuesApi.updateIssue(issueId, { description: finalDescription } as any)
      }
      return created
    },
    onSuccess: async (response) => {
      insertIssueIntoProjectCaches(qc, projectId!, response.data.data)
      await Promise.all([
        invalidateIssueViews(qc, projectId!),
        qc.refetchQueries({ queryKey: ['issues', projectId!] }),
      ])
      setCreateOpen(false)
      setDefaultStatusId(undefined)
    },
  })

  const project = projectData?.data?.data
  const statuses = (statusesData?.data?.data || []).slice().sort((a, b) => a.position - b.position)
  const allIssues: Issue[] = issuesData?.data?.data || []
  const members = membersData?.data?.data || []
  const labels = labelsData?.data?.data || []
  const canManageMainIssues = hasFullAccess(user?.role)
  const canCreateMainIssues = canCreateMainIssue(user?.role)

  const issuesByStatus = statuses.reduce<Record<string, Issue[]>>((acc, s) => {
    acc[s.id] = allIssues
      .filter((i) => i.statusId === s.id)
      .sort((a, b) => a.position - b.position || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    return acc
  }, {})

  function handleDragStart(event: DragStartEvent) {
    const issue = allIssues.find((i) => i.id === event.active.id)
    if (issue && canMoveIssue(user?.role, issue, user?.id)) setActiveIssue(issue)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveIssue(null)
    const { active, over } = event
    if (!over) return
    const issueId = active.id as string
    const draggedIssue = allIssues.find((i) => i.id === issueId)
    if (!draggedIssue) return
    if (!canMoveIssue(user?.role, draggedIssue, user?.id)) return

    const overId = over.id as string
    const overIssue = allIssues.find((i) => i.id === overId)
    const targetStatusId = overIssue?.statusId || overId
    const targetStatusIssues = issuesByStatus[targetStatusId] || []

    if (!statuses.some((status) => status.id === targetStatusId)) return

    const overIndex = overIssue ? targetStatusIssues.findIndex((issue) => issue.id === overIssue.id) : targetStatusIssues.length
    const currentIndex = targetStatusIssues.findIndex((issue) => issue.id === issueId)
    const targetIndex =
      draggedIssue.statusId === targetStatusId && currentIndex !== -1 && overIndex > currentIndex
        ? Math.max(0, overIndex - 1)
        : Math.max(0, overIndex)

    if (draggedIssue.statusId === targetStatusId && currentIndex === targetIndex) return

    const targetStatus = statuses.find((status) => status.id === targetStatusId)
    if (
      draggedIssue.statusId !== targetStatusId &&
      targetStatus &&
      requiresStatusDetailsPopup({
        currentStatusName: draggedIssue.status?.name,
        nextStatusName: targetStatus.name,
        userRole: user?.role,
      })
    ) {
      setReadyTransition({ issue: draggedIssue, targetStatusId, targetIndex })
      return
    }

    updateMutation.mutate({ issueId, targetStatusId, targetIndex })
  }

  const isClient = user?.role === 'CLIENT'

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {project && (
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: project.color, display: 'inline-block' }} />
          )}
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {project?.name || 'Board'}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IssueSheetActions
            projectId={projectId!}
            projectIdentifier={project?.identifier}
            canImport={canManageMainIssues}
          />
          <Link
            to={`/projects/${projectId}/list`}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', background: '#f1f5f9', color: '#475569', fontSize: '13px', fontWeight: 500, textDecoration: 'none' }}
          >
            <LayoutList size={14} /> List
          </Link>
          <Link
            to={`/projects/${projectId}`}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', background: '#6366f1', color: '#fff', fontSize: '13px', fontWeight: 500, textDecoration: 'none' }}
          >
            <LayoutDashboard size={14} /> Board
          </Link>
          {canCreateMainIssues && (
            <Button variant="primary" size="sm" onClick={() => { setDefaultStatusId(undefined); setCreateOpen(true) }}>
              <Plus size={14} /> New Issue
            </Button>
          )}
        </div>
      </div>

      <IssueFiltersBar
        filters={filters}
        onChange={setFilters}
        members={members}
        labels={labels}
        currentUserId={user?.id}
        resultCount={allIssues.length}
      />

      {/* Board */}
      {isLoading ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '48px' }}>Loading…</div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', flex: 1, paddingBottom: '16px' }}>
            {statuses.map((status) => (
              <KanbanColumn
                key={status.id}
                status={status}
                issues={issuesByStatus[status.id] || []}
                isClient={!canCreateMainIssues}
                canDrag={(issue) => canMoveIssue(user?.role, issue, user?.id)}
                onAddClick={() => {
                  setDefaultStatusId(status.id)
                  setCreateOpen(true)
                }}
              />
            ))}
          </div>
          <DragOverlay>
            {activeIssue ? <KanbanCard issue={activeIssue} isDragging canDrag={canMoveIssue(user?.role, activeIssue, user?.id)} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <IssueForm
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={projectId!}
        statuses={statuses}
        defaultStatusId={defaultStatusId}
        onSubmit={(data) => createMutation.mutate(data)}
        isSubmitting={createMutation.isPending}
      />

      <ReadyForNextStageModal
        isOpen={!!readyTransition}
        issue={readyTransition?.issue || null}
        statuses={statuses}
        labels={labels}
        user={user}
        initialNextStatusId={readyTransition?.targetStatusId || null}
        lockNextStatus
        isSubmitting={readyForNextStageMutation.isPending}
        onClose={() => setReadyTransition(null)}
        onSubmit={({ targetStatusId, labelIds, timeLog, proof }) => {
          if (!readyTransition) return
          const targetStatus = statuses.find((status) => status.id === targetStatusId)
          if (!targetStatus) return
          readyForNextStageMutation.mutate({
            issueId: readyTransition.issue.id,
            issueTitle: readyTransition.issue.title,
            targetStatusName: targetStatus.name,
            targetStatusId,
            targetIndex: readyTransition.targetIndex,
            ...(labelIds ? { labelIds } : {}),
            ...(timeLog ? { timeLog } : {}),
            ...(proof ? { proof } : {}),
          })
        }}
      />
    </div>
  )
}
